// The HUD panel slot (@rpg/contracts' HudPanelSlot; decision 0078): features add their own panels to the HUD, and
// the HUD imports none of them. Each panel gets a host element of its own inside its anchor's container; panels in
// one anchor are kept in `orderPanels` order (priority, then the order they were added). The containers are made on
// first use, so a HUD with no panels has no extra DOM. Pure DOM plumbing over a small element interface, so a test
// drives it with a fake document.
import { orderPanels, type HudAnchor, type HudFrame, type HudPanel, type HudPanelInfo, type HudPanelSlot } from "@rpg/contracts";

/** The element surface the slot uses. */
export interface PanelEl {
  appendChild(child: this): unknown;
  remove(): void;
  setAttribute(name: string, value: string): void;
  className: string;
}

/** Where the slot puts things. */
export interface PanelSlotOptions<E extends PanelEl> {
  /** Make an element. */
  create(): E;
  /** The parent an anchor's container goes into (called on first use and again by `reattach`). */
  parentFor(anchor: HudAnchor): E;
}

/** The slot plus what the HUD drives each frame. */
export interface HudPanels<E extends PanelEl> extends HudPanelSlot<E> {
  /** Each HUD frame: every panel's `update`, in order. */
  update(frame: HudFrame): void;
  /** True while any panel asks to keep the HUD awake. */
  awake(): boolean;
  /** Put every anchor's container back under its parent (after the HUD re-rendered its structure). */
  reattach(): void;
  /** Remove every panel (each one's `dispose`) and the containers. */
  dispose(): void;
}

/** A planted defect: panels are kept in the order they were added, ignoring priority (the slot's order check must go
 *  red). Off by default. */
export const HUD_PANEL_FAULTS = { ignorePriority: false };

/** Create the panel slot. */
export function createHudPanels<E extends PanelEl>(o: PanelSlotOptions<E>): HudPanels<E> {
  interface Entry {
    panel: HudPanel<E>;
    host: E;
    seq: number;
  }
  const entries: Entry[] = [];
  const containers = new Map<HudAnchor, E>();
  let seq = 0;
  const containerFor = (anchor: HudAnchor): E => {
    let c = containers.get(anchor);
    if (!c) {
      c = o.create();
      c.className = "rpg-hud-panels rpg-hud-fade";
      c.setAttribute("data-hud-anchor", anchor);
      o.parentFor(anchor).appendChild(c);
      containers.set(anchor, c);
    }
    return c;
  };
  const ordered = (): Entry[] => (HUD_PANEL_FAULTS.ignorePriority ? [...entries].sort((a, b) => a.seq - b.seq) : orderPanels(entries.map((e) => ({ ...e, anchor: e.panel.anchor, priority: e.panel.priority ?? 0 }))));
  /** Re-append an anchor's hosts in order (appending moves an element, so this sorts them in place). */
  const arrange = (anchor: HudAnchor): void => {
    const c = containerFor(anchor);
    for (const e of ordered()) if (e.panel.anchor === anchor) c.appendChild(e.host);
  };
  return {
    addPanel(panel) {
      if (entries.some((e) => e.panel.id === panel.id)) throw new Error(`hud: a panel "${panel.id}" is already on the HUD`);
      const host = o.create();
      host.className = "rpg-hud-panel";
      host.setAttribute("data-hud-panel", panel.id);
      const entry: Entry = { panel, host, seq: seq++ };
      entries.push(entry);
      arrange(panel.anchor);
      panel.mount(host);
      return () => {
        const i = entries.indexOf(entry);
        if (i < 0) return;
        entries.splice(i, 1);
        panel.dispose?.();
        host.remove();
      };
    },
    panels(): HudPanelInfo[] {
      return ordered().map((e) => ({ id: e.panel.id, anchor: e.panel.anchor, priority: e.panel.priority ?? 0 }));
    },
    update(frame) {
      for (const e of ordered()) e.panel.update?.(frame);
    },
    awake: () => entries.some((e) => e.panel.awake?.() === true),
    reattach() {
      for (const [anchor, c] of containers) o.parentFor(anchor).appendChild(c);
    },
    dispose() {
      for (const e of [...entries]) {
        e.panel.dispose?.();
        e.host.remove();
      }
      entries.length = 0;
      for (const c of containers.values()) c.remove();
      containers.clear();
    },
  };
}
